import {computed, inject, Injectable, signal} from '@angular/core';
import {
  EnumTranslationMapperService,
  TranslatedOption,
} from '../../../../shared/services/enum-translation-mapper.service';
import {
  ProjectRoleType,
  ProjectUserStatusType,
  UserInvitationTemplateControllerService,
  UserInvitationTemplateResponse,
} from '@frontend/shared-api';
import {finalize, forkJoin, map, Observable, of, tap} from 'rxjs';
import {DirectusService} from '@frontend/directus';
import {TranslocoService} from '@jsverse/transloco';

export const USER_INVITATION_TEMPLATE_PREFIX = 'projectUserInvitation_';

@Injectable({
  providedIn: 'root',
})
export class ProjectUserService {
  private readonly enumTranslationService = inject(EnumTranslationMapperService);
  private readonly directusService = inject(DirectusService);
  private readonly invitationTemplateBeService = inject(UserInvitationTemplateControllerService);
  private readonly transloco = inject(TranslocoService);

  loading = signal(false);
  rolesMap = signal<Map<string, string>>(new Map());
  rolesList = signal<TranslatedOption<ProjectRoleType>[]>([]);
  readonly loadingTemplates = signal<boolean>(false);
  readonly roleTemplates = signal<Map<ProjectRoleType, string>>(new Map());
  readonly customTemplates = signal<UserInvitationTemplateResponse[]>([]);

  readonly notSavedOriginalTemplates = computed<Map<ProjectRoleType, string>>(() => {
    const customTemplateRoles = new Set(this.customTemplates().map((t) => t.role));
    const roleTemplatesMap = this.roleTemplates();
    const result = new Map<ProjectRoleType, string>();

    this.rolesList().forEach((role) => {
      if (customTemplateRoles.has(role.enum)) {
        return;
      }
      const content = roleTemplatesMap.get(role.enum);
      if (content) {
        result.set(role.enum, content);
      }
    });

    return result;
  });

  getStatusLabel(status: ProjectUserStatusType): string {
    return this.enumTranslationService.getStatusLabel(status);
  }

  loadRoles() {
    this.loading.set(true);

    this.enumTranslationService
      .getAllRoleOptions()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((roles: TranslatedOption<ProjectRoleType>[]) => {
        this.rolesList.set(roles);

        const map = new Map<string, string>();
        roles.forEach((option: TranslatedOption<ProjectRoleType>) =>
          map.set(option.enum, option.translation),
        );
        this.rolesMap.set(map);
      });
  }

  createGetDirectusTemplateRequest(
    role: ProjectRoleType,
  ): Observable<{role: ProjectRoleType; content: string}> {
    return this.directusService
      .loadTextTemplate(USER_INVITATION_TEMPLATE_PREFIX + role.toLowerCase())
      .pipe(
        map((template) => {
          const activeLang = this.transloco.getActiveLang();
          const defaultLang = this.transloco.getDefaultLang();

          const match = template?.translations.find((t) => t.language === activeLang);
          const fallback = template?.translations.find((t) => t.language === defaultLang);
          const chosen = match ?? fallback ?? template?.translations[0];

          return {role, content: chosen?.template ?? ''};
        }),
      );
  }

  fetchTemplatesForRoles(roles: TranslatedOption<ProjectRoleType>[]): Observable<void> {
    this.loadingTemplates.set(true);
    const templateRequests = roles.map((role: TranslatedOption<ProjectRoleType>) => {
      return this.createGetDirectusTemplateRequest(role.enum);
    });

    return forkJoin(templateRequests).pipe(
      map((results) => {
        const templatesMap = new Map<ProjectRoleType, string>();
        results.forEach((result) => {
          templatesMap.set(result.role, result.content);
        });

        this.roleTemplates.set(templatesMap);
      }),
      finalize(() => this.loadingTemplates.set(false)),
    );
  }

  loadCustomTemplates(projectId: number): Observable<UserInvitationTemplateResponse[]> {
    this.loading.set(true);

    return this.invitationTemplateBeService.getUserInvitationTemplateByProject({projectId}).pipe(
      map((templates) => {
        this.customTemplates.set(templates);
        return templates;
      }),
      finalize(() => this.loading.set(false)),
    );
  }

  findCustomTemplateByRole(roleEnum: ProjectRoleType): UserInvitationTemplateResponse | undefined {
    return this.customTemplates().find((template) => template.role === roleEnum);
  }

  getTemplateContentForRole(roleEnum: ProjectRoleType): string {
    const customTemplate = this.findCustomTemplateByRole(roleEnum);
    if (customTemplate) {
      return customTemplate.content ?? '';
    }

    return this.notSavedOriginalTemplates().get(roleEnum) ?? '';
  }

  saveTemplateContent(
    projectId: number,
    role: ProjectRoleType,
    content: string,
    templateId?: number | null,
  ): Observable<void> {
    const isUpdate = templateId != null;

    if (isUpdate && templateId == null) {
      return of(undefined);
    }

    const request$ = isUpdate
      ? this.invitationTemplateBeService.update({
          projectId,
          templateId,
          userInvitationTemplateUpdateRequest: {content},
        })
      : this.invitationTemplateBeService.create({
          projectId,
          userInvitationTemplateCreateRequest: {role, content},
        });

    return request$.pipe(
      tap((savedTemplate: UserInvitationTemplateResponse) =>
        this.updateCustomTemplates(savedTemplate, role, templateId ?? undefined),
      ),
      map(() => undefined),
    );
  }

  private updateCustomTemplates(
    updatedTemplate: UserInvitationTemplateResponse,
    role: ProjectRoleType,
    templateId?: number,
  ): void {
    const templates = [...this.customTemplates()];
    const index = templates.findIndex((t) =>
      templateId != null ? t.id === templateId : t.role === role,
    );

    if (index >= 0) {
      templates[index] = updatedTemplate;
    } else {
      templates.push(updatedTemplate);
    }

    this.customTemplates.set(templates);
  }

  saveAllOriginalTemplates(projectId: number): Observable<void> {
    const templatesMap = this.notSavedOriginalTemplates();
    if (templatesMap.size === 0) {
      return of(undefined);
    }
    this.loading.set(true);

    const saveRequests: Observable<UserInvitationTemplateResponse>[] = [];
    templatesMap.forEach((content, role) => {
      const saveRequest = this.invitationTemplateBeService.create({
        projectId,
        userInvitationTemplateCreateRequest: {
          content,
          role,
        },
      });

      saveRequests.push(saveRequest);
    });

    return forkJoin(saveRequests).pipe(
      tap((savedTemplates) => {
        const currentTemplates = [...this.customTemplates()];
        const updatedTemplates = [...currentTemplates, ...savedTemplates];
        this.customTemplates.set(updatedTemplates);
      }),
      map(() => undefined),
      finalize(() => this.loading.set(false)),
    );
  }
}
